import {
  GoogleInitOptions,
  GoogleLoginProvider,
  SocialAuthServiceConfig,
  SocialLoginModule,
} from '@abacritt/angularx-social-login';
import { LayoutModule } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  InjectionToken,
  NgModule,
} from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import {
  MetaReducer,
  StoreModule,
  USER_PROVIDED_META_REDUCERS,
} from '@ngrx/store';
import { circuitBreakerMetaReducer } from './circuit-breaker.metareducer';
import {
  isCustomerUiAppMode,
  isCustomerUiPath,
} from './public/store/utils/CustomerUiContext';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  TranslateLoader,
  TranslateModule,
  TranslateParser,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ActionReducer } from 'ngx-bootstrap/mini-ngrx';
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';
import { APPMODE } from 'src/environments/appmode.enum';
import { environment } from '../environments/environment';
import { ApiInterceptor } from './api/api.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApplicationStateModule } from './application-state/application-state.module';
import { AuthActionType } from './auth/+state/auth.actions';
import { authReducer } from './auth/+state/auth.reducer';
import { AuthModule } from './auth/auth.module';
import { AUTH_CONFIG_TOKEN } from './auth/auth.tokens';
import { CustomerTokenInterceptor } from './auth/customer-token.interceptor';
import { TokenInterceptor } from './auth/token.interceptor';
import { CustomErrorHandler } from './error/custom-error-handler';
import { ErrorInterceptor } from './error/error.interceptor';
import { XoErrorInterceptor } from './error/xo-error.interceptor';
import { ErrorModule } from './error/error.module';
import { goAdminAppImports } from './goadmin-app.module';
import { goAppImports } from './gonnaorder-app.module';
import { LoaderModule } from './loader/loader.module';
import { WINDOW_PROVIDERS } from './public/window-providers';
import { FacebookLoginProvider } from './shared/custom-facebook-login.provider';
import { LogService } from './shared/logging/LogService';
import { SharedModule } from './shared/shared.module';
import { ViewportService } from './shared/viewport.service';
import { WhitelabelService } from './shared/whitelabel.service';
import { StripeVerificationComponent } from './stripe-verification.component';
import { goTemplateAppImports } from './template-app.module';
import { CustomParser } from './translate.parser';
import {
  NgxMaskDirective,
  NgxMaskPipe,
  provideEnvironmentNgxMask,
  provideNgxMask,
} from 'ngx-mask';
import { HelperService } from './public/helper.service';
import { CustomCookieService } from './custom-cookie-service';
import { RAW_COOKIE_SERVICE } from './raw-cookie-service.token';
import { GoogleMapsLoaderService } from './shared/google-maps-loader.service';
import { GoogleMapsApiKeyService } from './google-maps-api-key.service';
import { adminLanguages } from './language-helper';
import { StoreLanguageService } from './public/store-language.service';
import { Capacitor } from '@capacitor/core';

export function initializeGoogleMaps(
  loader: GoogleMapsLoaderService,
  apiKeyService: GoogleMapsApiKeyService,
) {
  return () => {
    const apiKey = apiKeyService.getApiKey();
    // Angular waits on every APP_INITIALIZER before rendering, so a rejected
    // load would leave the storefront blank. A Maps outage only degrades the
    // map features; the map consumers check window.google.maps themselves.
    return loader.loadGoogleMaps(apiKey).catch(() => undefined);
  };
}

export { RAW_COOKIE_SERVICE } from './raw-cookie-service.token';

/* Logs all state changes */
export function debugStateFactory(logger: LogService): MetaReducer<any> {
  return (reducer: ActionReducer<any>): ActionReducer<any> => {
    return (state, action) => {
      return reducer(state, action);
    };
  };
}

/**
 * Drops every slice when a backoffice session ends, so one administrator's
 * data cannot survive into the next session.
 *
 * The customer UI is deliberately exempt (card #11440). On the admin origin a
 * storefront page's requests carry the backoffice JWT, so a backoffice session
 * can end there too (card #11561), and wiping here would leave every mounted
 * component holding a store that no longer exists, throwing on each
 * change-detection pass. The customer session has its own action,
 * `SocialLogout`, which the customer reducers already handle, so nothing is
 * left behind by exempting them.
 */
export function clearState(reducer) {
  return (state, action) => {
    if (
      action.type === AuthActionType.LogoutSuccess &&
      !isCustomerUiAppMode() &&
      !isCustomerUiPath(window.location.pathname)
    ) {
      state = undefined;
    }

    return reducer(state, action);
  };
}

export function getMetaReducers(logger: LogService): MetaReducer<any>[] {
  return [circuitBreakerMetaReducer, debugStateFactory(logger), clearState];
}

const googleLoginOptions: GoogleInitOptions = {
  oneTapEnabled: false,
  prompt: '',
  scopes: 'https://www.googleapis.com/auth/userinfo.profile',
};

export async function provideSocialAuthConfig(
  whitelabelService: WhitelabelService,
  helperService: HelperService,
) {
  await new Promise<void>((resolve) => setTimeout(resolve, 500));
  return whitelabelService.waitForInit().then(() => {
    if (!helperService.isMobileApp()) {
      environment.googleClientId = whitelabelService.getGoogleCliendId();
    }

    environment.facebookClientId = whitelabelService.getFacebookAppId();
    environment.appleClientId = whitelabelService.getAppleAppId();

    return {
      autoLogin: false,
      providers: [
        {
          id: GoogleLoginProvider.PROVIDER_ID,
          provider: new GoogleLoginProvider(
            environment.googleClientId,
            googleLoginOptions,
          ),
        },
        {
          id: FacebookLoginProvider.PROVIDER_ID,
          provider: new FacebookLoginProvider(environment.facebookClientId),
        },
      ],
      onError: (e) => {
        if (e.error === 'idpiframe_initialization_failed') {
          document
            .querySelector('.google-btn')
            .setAttribute('style', 'pointer-events: none; opacity: 0.4;');
        }
      },
    };
  });
}

export function initializeApp(
  whitelabelService: WhitelabelService,
  router: Router,
  location: Location,
) {
  return () =>
    new Promise<void>((resolve) => {
      whitelabelService.loadWhiteLabelData().then(() => {
        resolve();
        const initialHostname = window.location.hostname;

        // Monitor domain changes
        location.onUrlChange(() => {
          if (window.location.hostname !== initialHostname) {
            whitelabelService.loadWhiteLabelData().then(() => resolve());
          }
        });
      });
    });
}

export function initTranslations(loader: StoreLanguageService) {
  return () => loader.loadCustomerUiLanguage();
}

@NgModule({
  declarations: [AppComponent, StripeVerificationComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    LayoutModule,
    ToastrModule.forRoot({
      positionClass:
        environment.mode !== APPMODE.Web
          ? 'toast-top-center-mobile'
          : 'toast-top-right',
    }),
    ApplicationStateModule,
    AuthModule,
    ErrorModule,
    LoaderModule,
    SocialLoginModule,
    StoreModule.forRoot(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      },
    ),
    StoreModule.forFeature('auth', authReducer, AUTH_CONFIG_TOKEN),
    EffectsModule.forRoot([]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
      parser: { provide: TranslateParser, useClass: CustomParser },
    }),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 250,
          logOnly: environment.production,
          connectInZone: true,
          trace: true,
        })
      : [],
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !Capacitor.isNativePlatform(),
    }),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ...goAdminAppImports,
    ...goTemplateAppImports,
    ...goAppImports,
  ],
  providers: [
    {
      provide: USER_PROVIDED_META_REDUCERS,
      deps: [LogService],
      useFactory: getMetaReducers,
    },
    {
      provide: 'SocialAuthServiceConfig',
      useFactory: provideSocialAuthConfig,
      deps: [WhitelabelService, HelperService],
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomerTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XoErrorInterceptor,
      multi: true,
    },
    WhitelabelService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [WhitelabelService, Router, Location],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGoogleMaps,
      deps: [GoogleMapsLoaderService, GoogleMapsApiKeyService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [StoreLanguageService],
      multi: true,
    },
    {
      provide: RAW_COOKIE_SERVICE,
      useClass: CookieService,
    },
    CustomCookieService,
    {
      provide: CookieService,
      useExisting: CustomCookieService,
    },
    WINDOW_PROVIDERS,
    ViewportService,
    provideNgxMask(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(private translate: TranslateService) {
    this.translate.addLangs(adminLanguages);
    let browserLang = this.translate.getBrowserLang();

    if (!this.translate.getLangs().includes(browserLang)) {
      browserLang = 'en';
    }
    this.translate.use(browserLang);
    this.translate.setDefaultLang('en');
  }
}

export function HttpLoaderFactory(httpBackend: HttpBackend) {
  return new TranslateHttpLoader(
    new HttpClient(httpBackend),
    '/assets/translations/i18n/admin-translation.',
    '.json',
  );
}
